/**
 * Сторож намеренного удаления учётной записи (§130, stage9-account-delete).
 * Демон закрывает потоки пользователя кодом 4401 раньше, чем отвечает `204`, и
 * клиент не должен показывать на форме входа «Сессия истекла» после
 * удаления, которое человек сам и запросил. Порядок событий (4401 до 204 или
 * после) значения не имеет: пока удаление идёт или удалось, уведомление об
 * истечении не ставится.
 *
 * У каждой попытки удаления свой номер. Новый вход (`reset`) номер сдвигает:
 * поздний ответ прежней попытки (`204` или отказ после нового входа) уже не
 * относится к текущей сессии и не применяется — иначе он сбросил бы сессию,
 * только что открытую человеком. Сторож не пересчитывает ничего доменного.
 */
export const SESSION_EXPIRED_NOTICE = "Сессия истекла. Войдите снова.";

export interface DeletionGuard {
  /** Запрос удаления отправлен; возвращает номер попытки. */
  begin: () => number;
  /**
   * Удаление удалось (`204`). `true` — попытка текущая, исход применяется.
   * Сторож остаётся включённым: поздний 4401 тоже молчит до нового входа.
   */
  settleDeleted: (attempt: number) => boolean;
  /**
   * Удаление не состоялось (отказ, `401`, недоступность): 4401 снова значит
   * истечение. `true` — попытка текущая, исход применяется.
   */
  settleFailed: (attempt: number) => boolean;
  /** Новый вход: прежнее удаление забыто, его поздний исход не применяется. */
  reset: () => void;
  /** Уведомление для формы входа при закрытии сессии потоком или отказом `401`. */
  noticeForSessionInvalid: () => string | null;
}

export function createDeletionGuard(): DeletionGuard {
  let current = 0;
  let deleting = false;
  return {
    begin() {
      current += 1;
      deleting = true;
      return current;
    },
    settleDeleted(attempt) {
      return attempt === current;
    },
    settleFailed(attempt) {
      if (attempt !== current) {
        return false;
      }
      deleting = false;
      return true;
    },
    reset() {
      current += 1;
      deleting = false;
    },
    noticeForSessionInvalid() {
      return deleting ? null : SESSION_EXPIRED_NOTICE;
    },
  };
}
